// Menu and dialogs: departments, note layers, show title and page labels, what's shown, save status.

import { LogOut, UserRound } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { newId, recordHash } from "../../format/ids.ts";
import { labelPages } from "../../format/labels.ts";
import type { Department, NoteLayer } from "../../format/types.ts";
import { COLOR_SUGGESTIONS, flushDraft, freshColor, retargetStandbys } from "../actions.ts";
import { type Change, commit, del, put } from "../edits.ts";
import { offline } from "../offline.ts";
import { post } from "../account.ts";
import { dialog, ix, notify, pending, proposals, show, status, view } from "../state.ts";
import { discardHeld, downloadPending, kick, releaseHeld, resync, retryOffline } from "../sync.ts";
import { confirmAction } from "./Confirmation.tsx";
import { ExportDialog } from "./Export.tsx";
import { History, Members, Proposals, ShowSettings } from "./ShowDialogs.tsx";
import { ActionRow, Group, InfoRow, NavRow, Sheet, SwitchRow } from "./kit.tsx";

/** A screen reached from the menu: it gets a "‹ Menu" back button. */
function Modal(props: { title: string; children: ComponentChildren; wide?: boolean }) {
  return (
    <Sheet title={props.title} back="menu" wide={props.wide}>
      {props.children}
    </Sheet>
  );
}

export async function checkpoint(): Promise<void> {
  const name = prompt("Checkpoint name (e.g. after Act 1 paper tech):")?.trim();
  if (!name) return;
  if (pending.value.some((e) => !e.held)) notify("Changes still waiting on this device aren't in this checkpoint; it marks the last saved version.", "warn");
  try {
    const r = await post<{ revision: number }>(`/api/shows/${show.value.showId}/checkpoints`, { name });
    notify(`Checkpoint "${name}" marks saved version ${r.revision}.`);
  } catch (e) {
    notify(`Checkpoint failed: ${(e as Error).message}`, "error");
  }
}

function saveDetail(): string {
  const n = pending.value.length;
  if (status.value.kind === "error") return "Not saved";
  if (proposals.value.length) return `${proposals.value.length} to review`;
  return n ? `${n} waiting` : show.value.lock ? "Read-only" : "All saved";
}

function Menu() {
  const index = ix.value;
  const go = (d: typeof dialog.value) => () => (dialog.value = d);
  const codes = index?.sortedDepartments.map((d) => d.code).join(", ") ?? "";
  const layers = index?.sortedLayers.length ?? 0;
  return (
    <Sheet title="Menu">
      <Group title="Show">
        <NavRow label="Title and page numbers" detail={index?.doc.project.title} onClick={go("project")} />
        <NavRow label="Departments" detail={codes} onClick={go("departments")} />
        <NavRow label="Note layers" detail={layers ? String(layers) : "None yet"} onClick={go("layers")} />
      </Group>
      <ShowHide />
      <Group title="Output">
        <NavRow label="Export prompt book and cue sheets" onClick={go("export")} />
      </Group>
      <Group title="Collaboration">
        <NavRow label="People" detail={show.value.role} onClick={go("members")} />
        <NavRow label="History and checkpoints" onClick={go("history")} />
      </Group>
      <Group title="Saving" footer="A checkpoint names the last saved version, e.g. “after Act 1 paper tech”. History keeps every saved change.">
        <NavRow label="Save status" detail={saveDetail()} onClick={go("status")} />
        {proposals.value.length > 0 && <NavRow label="Changes to review" detail={String(proposals.value.length)} onClick={go("proposals")} />}
        <ActionRow
          label="Make a checkpoint…"
          disabled={show.value.role === "viewer" || show.value.state !== "active"}
          onClick={() => {
            dialog.value = null;
            void checkpoint();
          }}
        />
      </Group>
      <Group title="This show">
        <NavRow label="Show settings, bundle and lifecycle" onClick={go("show")} />
        <ActionRow label="All shows" onClick={() => (location.href = "/")} />
      </Group>
      <Group title="Your account">
        <a class="row nav" href="/account"><UserRound class="account-icon" aria-hidden="true" /><span class="row-label">Account</span><span class="chevron" aria-hidden="true">›</span></a>
        <NavRow label="Sign out" icon={<LogOut class="account-icon" aria-hidden="true" />} onClick={async () => {
          try {
            await flushDraft();
            location.href = "/account#sign-out";
          } catch (error) {
            notify(`Couldn't keep this draft on this device. Sign-out hasn't started. ${(error as Error).message}`, "error");
          }
        }} />
      </Group>
      <Group title="Help">
        <NavRow label="Keyboard shortcuts" onClick={go("shortcuts")} />
      </Group>
      <Group title="Version">
        <InfoRow label="q2cue" detail={__Q2CUE_VERSION__} />
        {__Q2CUE_COMMIT__ && <InfoRow label="Build" detail={__Q2CUE_COMMIT__.slice(0, 7)} />}
      </Group>
    </Sheet>
  );
}

function Shortcuts() {
  const reference = useRef<HTMLDivElement>(null);
  useEffect(() => {
    reference.current?.focus({ preventScroll: true });
  }, []);
  return (
    <Modal title="Keyboard shortcuts">
      <div ref={reference} class="shortcuts-reference" tabIndex={-1} role="document" aria-label="Keyboard shortcut reference">
        <p class="group-foot">Click the PDF or Tab to <strong>Script PDF</strong> before using single-character shortcuts.</p>
        <Group title="Scrolling" footer="Hold these keys to keep scrolling. PDF and annotation labels move together.">
          <InfoRow label="↑ / ↓" detail="Scroll up / down a little." />
          <InfoRow label="← / →" detail="Pan left / right when zoomed in." />
          <InfoRow label="Page Up / Page Down" detail="Scroll one screen up / down, with overlap." />
        </Group>
        <Group title="Tools" footer="Changing tools never saves or discards a draft. Select cues requires editing permission, no open draft and Preview off.">
          <InfoRow label="I / P" detail="Choose Interact / Place." />
          <InfoRow label="S" detail="Select cues. Press again to stay in this mode." />
          <InfoRow label="V" detail="Toggle Preview; holding does not toggle repeatedly." />
          <InfoRow label="C" detail="Show the cue list. Press again to keep it open, or to return from Notes to Cues." />
          <InfoRow label="M" detail="Open Menu. Press again to keep it open." />
        </Group>
        <Group title="PDF pages and zoom" footer="Page and zoom keys repeat while held. Page jumps use actual PDF pages, not printed numbers.">
          <InfoRow label="[ / ]" detail="Previous / next PDF page." />
          <InfoRow label="− / + (or =)" detail="Zoom out / in." />
          <InfoRow label="F" detail="Fit width." />
        </Group>
        <Group title="Help">
          <InfoRow label="?" detail="Show this list. Also in Menu → Keyboard shortcuts." />
          <InfoRow label="Escape" detail="Close the cue list, Menu or this reference and return focus to the PDF. Open drafts stay unchanged." />
        </Group>
        <p class="group-foot">PDF shortcuts leave typing and controls alone. With the cue drawer open, C returns to Cues and M opens Menu if the PDF has focus; Escape closes the drawer. Other dialogs keep their own controls. Scrolling also works with focus on the document body.</p>
        <p class="group-foot">For a dial or macro pad, send these ordinary keys without Ctrl, Alt or Meta/Command. Use Shift for + and ?. Bindings are fixed.</p>
      </div>
    </Modal>
  );
}

/** Which departments, note layers and standbys this device shows. Not project data. */
function ShowHide() {
  const index = ix.value;
  if (!index) return null;
  const v = view.value;
  const set = (list: string[], id: string, on: boolean) => (on ? list.filter((x) => x !== id) : [...new Set([...list, id])]);
  return (
    <Group title="Show on this device" footer="Hiding something here only changes what this screen shows; nothing is deleted.">
      {index.sortedDepartments.map((d) => (
        <SwitchRow
          label={d.code}
          detail={d.name !== d.code ? d.name : undefined}
          color={d.color}
          checked={!v.hiddenDepartments.includes(d.id)}
          onChange={(on) => (view.value = { ...v, hiddenDepartments: set(v.hiddenDepartments, d.id, on) })}
        />
      ))}
      {index.sortedLayers.map((l) => (
        <SwitchRow
          label={l.name}
          detail="notes"
          color={l.color}
          checked={!v.hiddenLayers.includes(l.id)}
          onChange={(on) => (view.value = { ...v, hiddenLayers: set(v.hiddenLayers, l.id, on) })}
        />
      ))}
    </Group>
  );
}

/** Reassign orders 1..n after moving item `i` by `delta`. One undo step. */
function reorder<T extends Department | NoteLayer>(type: "departments" | "noteLayers", sorted: T[], i: number, delta: number): void {
  const j = i + delta;
  if (j < 0 || j >= sorted.length) return;
  const list = [...sorted];
  [list[i], list[j]] = [list[j] as T, list[i] as T];
  const changes: Change[] = [];
  list.forEach((item, k) => {
    if (item.order !== k + 1) changes.push(put(type, { ...item, order: k + 1 } as T, item));
  });
  commit(type === "departments" ? "Reorder departments" : "Reorder note layers", changes);
}

function DepartmentColorPicker(props: { department: Department; onChange: (color: string) => void }) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const colorName = COLOR_SUGGESTIONS.find((c) => c.color === props.department.color)?.name ?? props.department.color;
  const choose = (color: string) => {
    props.onChange(color);
    popup.current?.hidePopover();
  };
  return (
    <>
      <button ref={button} type="button" class="department-color-button" style={{ background: props.department.color }} popovertarget={id} aria-label={`Color for ${props.department.code}: ${colorName}`} />
      <div
        ref={popup}
        id={id}
        popover="auto"
        class="department-color-popup"
        role="dialog"
        aria-label={`Color for ${props.department.code}`}
        onToggle={() => {
          const el = popup.current;
          const anchor = button.current;
          if (!el?.matches(":popover-open") || !anchor) return;
          const rect = anchor.getBoundingClientRect();
          const bounds = el.getBoundingClientRect();
          el.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - bounds.width - 8))}px`;
          el.style.top = `${Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - bounds.height - 8))}px`;
        }}
      >
        <div class="department-color-swatches">
          {COLOR_SUGGESTIONS.map((c) => (
            <button key={c.color} type="button" class="department-color-swatch" style={{ background: c.color }} aria-label={c.name} aria-pressed={props.department.color === c.color} onClick={() => choose(c.color)}>
              {props.department.color === c.color && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
        <label class="department-custom-color">
          <input type="color" aria-label="Custom department color" value={props.department.color} onChange={(e) => choose((e.target as HTMLInputElement).value.toLowerCase())} />
          Custom…
        </label>
      </div>
    </>
  );
}

function Departments() {
  const index = ix.value;
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ code: "", name: "" });
  if (!index) return null;
  const depts = index.sortedDepartments;
  const codeOk = (code: string, id: string | null) => {
    if (!/^[A-Za-z0-9]{1,6}$/.test(code)) return "A code is 1–6 letters or digits.";
    if (index.doc.departments.some((d) => d.id !== id && d.code.toUpperCase() === code.toUpperCase())) return `${code} is already used.`;
    return null;
  };
  const edit = (d: Department, patch: Partial<Department>) => {
    const after = { ...d, ...patch };
    if (patch.code !== undefined) {
      const err = codeOk(after.code, d.id);
      setError(err);
      if (err) return;
    }
    commit(`Edit department ${after.code}`, [put("departments", after, d)]);
  };
  const remove = async (d: Department) => {
    const showId = show.value.showId;
    const cues = index.doc.cues.filter((c) => c.department === d.id);
    const departmentHash = recordHash("departments", d);
    const cueHashes = new Map(cues.map((c) => [c.id, recordHash("cues", c)]));
    if (cues.length && !(await confirmAction({ title: "Delete department", message: `Delete ${d.code} and its ${cues.length} cue${cues.length === 1 ? "" : "s"}? (Undo brings them back.)`, confirmLabel: "Delete department", destructive: true }))) return;
    const current = ix.value;
    const department = current?.doc.departments.find((item) => item.id === d.id);
    if (!current || !department || show.value.showId !== showId) return;
    const currentCues = current.doc.cues.filter((c) => c.department === d.id);
    if (recordHash("departments", department) !== departmentHash || currentCues.length !== cueHashes.size || currentCues.some((c) => cueHashes.get(c.id) !== recordHash("cues", c))) {
      setError("This department or its cues changed while you were confirming. Review them and try again.");
      return;
    }
    const gone = new Set(currentCues.map((c) => c.id));
    const changes: Change[] = currentCues.map((c) => del("cues", c));
    for (const m of current.doc.moments) {
      const left = current.cuesOf(m.id).filter((c) => !gone.has(c.id));
      if (current.cuesOf(m.id).length && !left.length) changes.push(del("moments", m));
    }
    changes.push(...retargetStandbys(current, gone));
    changes.push(del("departments", department));
    commit(`Delete department ${department.code}`, changes);
  };
  const add = () => {
    const code = draft.code.trim();
    const err = codeOk(code, null);
    setError(err);
    if (err) return;
    const d: Department = { id: newId("departments"), code, name: draft.name.trim() || code, color: freshColor(index), order: Math.max(0, ...depts.map((x) => x.order)) + 1 };
    commit(`New department ${code}`, [put("departments", d)]);
    setDraft({ code: "", name: "" });
  };
  return (
    <Modal title="Departments" wide>
      <table class="manage">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Color</th>
            <th>Cues</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {depts.map((d, i) => (
            <tr key={d.id}>
              <td>
                <input class="code-input" value={d.code} onChange={(e) => edit(d, { code: (e.target as HTMLInputElement).value.trim() })} />
              </td>
              <td>
                <input value={d.name} onChange={(e) => edit(d, { name: (e.target as HTMLInputElement).value })} />
              </td>
              <td>
                <DepartmentColorPicker department={d} onChange={(color) => edit(d, { color })} />
              </td>
              <td class="num">{index.doc.cues.filter((c) => c.department === d.id).length}</td>
              <td class="row-buttons">
                <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => reorder("departments", depts, i, -1)}>
                  ↑
                </button>
                <button type="button" aria-label="Move down" disabled={i === depts.length - 1} onClick={() => reorder("departments", depts, i, 1)}>
                  ↓
                </button>
                <button type="button" class="danger" onClick={() => void remove(d)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <input class="code-input" placeholder="Code" aria-label="New department code" value={draft.code} onInput={(e) => setDraft({ ...draft, code: (e.target as HTMLInputElement).value })} />
            </td>
            <td>
              <input placeholder="Name, e.g. Followspot" aria-label="New department name" value={draft.name} onInput={(e) => setDraft({ ...draft, name: (e.target as HTMLInputElement).value })} />
            </td>
            <td colSpan={3}>
              <button type="button" class="primary" onClick={add}>Add department</button>
            </td>
          </tr>
        </tbody>
      </table>
      {error && <div class="error">{error}</div>}
    </Modal>
  );
}

function Layers() {
  const index = ix.value;
  const [name, setName] = useState("");
  if (!index) return null;
  const layers = index.sortedLayers;
  const edit = (l: NoteLayer, patch: Partial<NoteLayer>) => commit(`Edit layer ${l.name}`, [put("noteLayers", { ...l, ...patch }, l)]);
  const remove = async (l: NoteLayer) => {
    const showId = show.value.showId;
    const notes = index.doc.notes.filter((n) => n.layer === l.id);
    const layerHash = recordHash("noteLayers", l);
    const noteHashes = new Map(notes.map((n) => [n.id, recordHash("notes", n)]));
    if (notes.length && !(await confirmAction({ title: "Delete note layer", message: `Delete the ${l.name} layer and its ${notes.length} note${notes.length === 1 ? "" : "s"}? (Undo brings them back.)`, confirmLabel: "Delete note layer", destructive: true }))) return;
    const current = ix.value;
    const layer = current?.doc.noteLayers.find((item) => item.id === l.id);
    if (!current || !layer || show.value.showId !== showId) return;
    const currentNotes = current.doc.notes.filter((n) => n.layer === l.id);
    if (recordHash("noteLayers", layer) !== layerHash || currentNotes.length !== noteHashes.size || currentNotes.some((n) => noteHashes.get(n.id) !== recordHash("notes", n))) {
      notify("This layer or its notes changed while you were confirming. Review them and try again.", "warn");
      return;
    }
    commit(`Delete layer ${layer.name}`, [...currentNotes.map((n) => del("notes", n)), del("noteLayers", layer)]);
  };
  const add = () => {
    const n = name.trim();
    if (!n) return;
    const l: NoteLayer = { id: newId("noteLayers"), name: n, color: freshColor(index), order: Math.max(0, ...layers.map((x) => x.order)) + 1 };
    commit(`New layer ${n}`, [put("noteLayers", l)]);
    setName("");
  };
  return (
    <Modal title="Note layers" wide>
      <table class="manage">
        <thead>
          <tr>
            <th>Name</th>
            <th>Color</th>
            <th>Notes</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {layers.map((l, i) => (
            <tr key={l.id}>
              <td>
                <input value={l.name} onChange={(e) => edit(l, { name: (e.target as HTMLInputElement).value })} />
              </td>
              <td>
                <input type="color" value={l.color} onChange={(e) => edit(l, { color: (e.target as HTMLInputElement).value.toLowerCase() })} />
              </td>
              <td class="num">{index.doc.notes.filter((n) => n.layer === l.id).length}</td>
              <td class="row-buttons">
                <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => reorder("noteLayers", layers, i, -1)}>
                  ↑
                </button>
                <button type="button" aria-label="Move down" disabled={i === layers.length - 1} onClick={() => reorder("noteLayers", layers, i, 1)}>
                  ↓
                </button>
                <button type="button" class="danger" onClick={() => void remove(l)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <input placeholder="Director, Blocking, Rewrites…" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
            </td>
            <td colSpan={3}>
              <button type="button" class="primary" onClick={add}>
                Add layer
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="hint">Layers are also created when you make a note.</p>
    </Modal>
  );
}

function Project() {
  const index = ix.value;
  const first = index?.doc.pages.find((p) => p.label);
  const [rule, setRule] = useState({ fromPage: first?.page ?? 1, first: Number(first?.label) || 1, step: 1 });
  if (!index) return null;
  const { project, pages } = index.doc;
  const preview = labelPages(pages, rule);
  const apply = () => {
    const changes: Change[] = [];
    preview.forEach((p, i) => {
      const before = pages[i];
      if (before && before.label !== p.label) changes.push(put("pages", p, before));
    });
    if (!changes.length) return notify("The page labels already match.");
    commit("Set page labels", changes);
    notify(`Page labels set (${changes.length} page${changes.length === 1 ? "" : "s"} changed).`);
  };
  const num = (v: string) => Math.trunc(Number(v)) || 0;
  return (
    <Modal title="Show" wide>
      <label class="field">
        Show title
        <input value={project.title} onChange={(e) => commit("Edit show title", [put("project", { ...project, title: (e.target as HTMLInputElement).value.trim() }, project)])} />
      </label>
      <p class="hint">
        Source: {project.source.path} · {project.source.pageCount} pages · sha256 {project.source.sha256.slice(0, 12)}…
      </p>
      <h3>Printed page numbers</h3>
      <p class="hint">PDF page {rule.fromPage} shows printed page {rule.first}; each following page adds {rule.step}. Earlier pages have no number.</p>
      <div class="label-rule">
        <label>
          PDF page <input type="number" min={1} max={pages.length} value={rule.fromPage} onInput={(e) => setRule({ ...rule, fromPage: num((e.target as HTMLInputElement).value) })} />
        </label>
        <label>
          shows <input type="number" value={rule.first} onInput={(e) => setRule({ ...rule, first: num((e.target as HTMLInputElement).value) })} />
        </label>
        <label>
          step <input type="number" value={rule.step} onInput={(e) => setRule({ ...rule, step: num((e.target as HTMLInputElement).value) })} />
        </label>
        <button type="button" class="primary" onClick={apply}>
          Apply
        </button>
      </div>
      <p class="hint">
        Preview:{" "}
        {preview
          .slice(0, 6)
          .map((p) => `${p.page} → ${p.label || "–"}`)
          .join(", ")}
        {preview.length > 6 ? ` … ${preview.at(-1)?.page} → ${preview.at(-1)?.label || "–"}` : ""}
      </p>
    </Modal>
  );
}

function Status() {
  const s = status.value;
  const waiting = pending.value.filter((e) => !e.held);
  const held = pending.value.filter((e) => e.held);
  const n = waiting.length;
  return (
    <Modal title="Saving">
      <p data-status={s.kind}>
        {s.kind === "saved" && !n && (show.value.lock ? show.value.lock : "Every change is saved.")}
        {s.kind === "saving" && `Saving ${n} change${n === 1 ? "" : "s"}…`}
        {s.kind === "offline" && `${s.message}. ${n} change${n === 1 ? " is" : "s are"} waiting on this device and will be sent when q2cue is reachable.`}
        {s.kind === "error" && s.message}
      </p>
      {(s.kind === "offline" || s.kind === "error") && (
        <div class="panel-buttons">
          <button type="button" onClick={() => void resync()}>
            Reconnect
          </button>
          {s.kind === "error" && /session/i.test(s.message) && (
            <a class="button primary" href={`/signin?return=${encodeURIComponent(location.pathname)}`} target="_blank" rel="opener">
              Sign in again
            </a>
          )}
        </div>
      )}
      {held.length > 0 && (
        <>
          <h3>Kept from before you signed out</h3>
          <p class="hint">These {held.length} change{held.length === 1 ? " was" : "s were"} never saved. Send them only if they still belong in the show; anything someone else changed since will come back for review.</p>
          <ul class="pending-list">
            {held.map((e) => (
              <li>
                {e.label} <span class="hint">{new Date(e.at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
          <div class="panel-buttons">
            <button type="button" onClick={downloadPending}>Download</button>
            <button
              type="button"
              class="danger"
              onClick={async () => {
                const confirmed = pending.value.filter((e) => e.held);
                const ids = new Set(confirmed.map((e) => e.id));
                if (!(await confirmAction({ title: "Discard held changes", message: `Discard ${confirmed.length} change${confirmed.length === 1 ? "" : "s"} that were never saved?`, confirmLabel: "Discard changes", destructive: true }))) return;
                const current = pending.value.filter((e) => e.held);
                if (current.length !== ids.size || current.some((e) => !ids.has(e.id))) {
                  notify("The held changes changed while you were confirming. Review them and try again.", "warn");
                  return;
                }
                await discardHeld(ids);
              }}
            >
              Discard
            </button>
            <span class="spacer" />
            <button type="button" class="primary" disabled={!!show.value.lock} onClick={() => void releaseHeld()}>
              Send them
            </button>
          </div>
        </>
      )}
      <h3>Offline use</h3>
      <p data-offline={offline.value.kind}>{offline.value.message}</p>
      <p class="hint">
        Offline copies include the private script and show data. Use a trusted device. Browser storage can be cleared or evicted; this is not a backup. Signing out removes downloaded scripts but keeps unsent changes for this account. Export, checkpoints and history need a connection.
      </p>
      {offline.value.kind === "unavailable" && <button type="button" onClick={retryOffline}>Try offline preparation again</button>}
      {s.kind === "error" && s.problems.length > 0 && (
        <ul class="problems">
          {s.problems.map((p) => (
            <li>{p}</li>
          ))}
        </ul>
      )}
      {n > 0 && (
        <>
          <ul class="pending-list">
            {waiting.map((e) => (
              <li>
                {e.label} <span class="hint">{new Date(e.at).toLocaleTimeString()}</span>
              </li>
            ))}
          </ul>
          <div class="panel-buttons">
            <button type="button" onClick={downloadPending}>
              Download unsaved changes
            </button>
            <span class="spacer" />
            <button type="button" class="primary" onClick={kick}>
              Retry now
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export function Dialogs() {
  switch (dialog.value) {
    case "menu":
      return <Menu />;
    case "shortcuts":
      return <Shortcuts />;
    case "departments":
      return <Departments />;
    case "layers":
      return <Layers />;
    case "project":
      return <Project />;
    case "status":
      return <Status />;
    case "export":
      return <ExportDialog />;
    case "members":
      return <Members />;
    case "history":
      return <History />;
    case "proposals":
      return <Proposals />;
    case "show":
      return <ShowSettings />;
    default:
      return null;
  }
}
